import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiTrash2, FiUsers } from "react-icons/fi";
import http from "../api/apiClient";

type ClassParent = {
  parentId: string;
  parentName: string;
  email?: string | null;
  familyName?: string | null;
  childNames?: string[];
};

type Overview = {
  classParents?: ClassParent[];
  canManage?: boolean;
};

const optionContext = (parent: ClassParent) => {
  const children = Array.isArray(parent.childNames) ? parent.childNames.filter(Boolean).join(", ") : "";
  return [children, parent.familyName].filter(Boolean).join(" · ");
};

const ClassParentsSection: React.FC<{ classroomId?: string | number | null; embedded?: boolean }> = ({
  classroomId,
  embedded = false,
}) => {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<Overview>({});
  const [candidates, setCandidates] = useState<ClassParent[]>([]);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!classroomId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await http.get<Overview>(`/classrooms/${classroomId}/class-parents`);
      const next = data || {};
      setOverview(next);
      if (next.canManage) {
        const candidateResponse = await http.get<ClassParent[]>(`/classrooms/${classroomId}/class-parents/candidates`);
        setCandidates(Array.isArray(candidateResponse.data) ? candidateResponse.data : []);
      } else {
        setCandidates([]);
      }
    } catch {
      setError(t("classrooms.class_parents.load_failed", { defaultValue: "Class parents could not be loaded." }));
    } finally {
      setLoading(false);
    }
  }, [classroomId, t]);

  useEffect(() => { load(); }, [load]);

  const linkedIds = useMemo(
    () => new Set((overview.classParents || []).map((parent) => String(parent.parentId))),
    [overview.classParents],
  );
  const availableCandidates = candidates.filter((parent) => !linkedIds.has(String(parent.parentId)));

  const add = async () => {
    if (!selectedParentId || !classroomId) return;
    setSaving(true);
    setError("");
    try {
      await http.post(`/classrooms/${classroomId}/class-parents`, { parentId: selectedParentId });
      setSelectedParentId("");
      await load();
    } catch {
      setError(t("classrooms.class_parents.save_failed", { defaultValue: "The class parent could not be saved." }));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (parentId: string) => {
    if (!classroomId) return;
    setSaving(true);
    setError("");
    try {
      await http.delete(`/classrooms/${classroomId}/class-parents/${parentId}`);
      await load();
    } catch {
      setError(t("classrooms.class_parents.remove_failed", { defaultValue: "The class parent could not be removed." }));
    } finally {
      setSaving(false);
    }
  };

  if (!classroomId) return null;

  return (
    <section className={embedded ? "mt-5" : "rounded-2xl border border-slate-200/80 bg-white p-5 dark:border-white/10 dark:bg-white/5"}>
      <div className="flex items-center gap-2">
        <FiUsers aria-hidden="true" className="text-emerald-600 dark:text-emerald-300" />
        <h2 className="text-base font-bold text-slate-950 dark:text-white">
          {t("classrooms.class_parents.title", { defaultValue: "Class parents" })}
        </h2>
      </div>

      {loading ? (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {t("common.loading", { defaultValue: "Loading..." })}
        </p>
      ) : (overview.classParents || []).length ? (
        <div className="mt-3 divide-y divide-slate-100 rounded-2xl border border-slate-100 dark:divide-slate-800 dark:border-slate-800">
          {(overview.classParents || []).map((parent) => (
            <div key={parent.parentId} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-slate-950 dark:text-white">{parent.parentName}</p>
                <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                  {[parent.email, optionContext(parent)].filter(Boolean).join(" · ")}
                </p>
              </div>
              {overview.canManage ? (
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => remove(parent.parentId)}
                  aria-label={t("classrooms.class_parents.remove", {
                    defaultValue: "Remove {{name}} as class parent",
                    name: parent.parentName,
                  })}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-red-600 transition hover:bg-red-50 disabled:opacity-50 dark:text-red-300 dark:hover:bg-red-950/40"
                >
                  <FiTrash2 aria-hidden="true" />
                </button>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {t("classrooms.class_parents.empty", { defaultValue: "No class parent has been assigned yet." })}
        </p>
      )}

      {overview.canManage ? (
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <select
            value={selectedParentId}
            onChange={(event) => setSelectedParentId(event.target.value)}
            aria-label={t("classrooms.class_parents.select", { defaultValue: "Select a parent from this classroom" })}
            className="min-h-11 min-w-0 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
          >
            <option value="">{t("classrooms.class_parents.select", { defaultValue: "Select a parent from this classroom" })}</option>
            {availableCandidates.map((parent) => (
              <option key={parent.parentId} value={parent.parentId}>
                {parent.parentName}{optionContext(parent) ? ` — ${optionContext(parent)}` : ""}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={!selectedParentId || saving}
            onClick={add}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FiPlus aria-hidden="true" />
            {t("classrooms.class_parents.add", { defaultValue: "Add class parent" })}
          </button>
        </div>
      ) : null}

      {error ? <p role="alert" className="mt-3 text-sm font-semibold text-red-600 dark:text-red-300">{error}</p> : null}
    </section>
  );
};

export default ClassParentsSection;
